import { get, head, tail, map, isArray } from 'lodash';
import classNames from 'classnames';
import React, { Component, PropTypes } from 'react';
import { setMetaTags } from 'lib/frankly';
import { bylineToString } from 'utils/NewsUtils';
import { filterItems } from 'utils/CategoryUtils';
import Image from 'components/Image';
import ComponentTitle from 'components/shared/ComponentTitle';
import ReadMoreButton from 'components/shared/ReadMoreButton';
import Timestamp from 'components/Timestamp';
import { showWindow, getDataOfWindowOpen } from 'utils/WindowUtils';
import { TITLE_TYPE } from '../../constants/FRNBaseStyles';
import { getCanonicalLink } from  'utils/SeoUtils';
/**
 * Class representing an overview of news articles coming from a specific news feed.
 */
class ArticleList extends Component {
  static propTypes = {
    FRN_rawResponses: PropTypes.array,
    FRN_flux: PropTypes.object,
    title: PropTypes.string,
    headline: PropTypes.string,
    layout: PropTypes.string,
    titleColor: PropTypes.string,
    backgroundColor: PropTypes.string,
    textColor: PropTypes.string,
    startArticleIndex: PropTypes.number,
    types: PropTypes.array,
    displaySizes: PropTypes.array,
    numberOfItems: PropTypes.number,
    showThumbnails: PropTypes.bool,
    showPillsOnHeroItem: PropTypes.bool,
    showDescriptions: PropTypes.bool,
    showTitle: PropTypes.bool,
    thumbnailAlignment: PropTypes.string,
    thumbnailHtmlWidth: PropTypes.number,
    showReadMore: PropTypes.bool,
    showMoreArrow: PropTypes.bool,
    readMoreText: PropTypes.string,
    readMoreBackgroundColor: PropTypes.string,
    readMoreTextColor: PropTypes.string,
    showBylines: PropTypes.bool,
    showUpdatedDate: PropTypes.bool,
    fill: PropTypes.string,
    categoryUrl: PropTypes.string,
    linkToLegacy: PropTypes.bool,
    enableStackView: PropTypes.bool,
    maintainHeroImageAspectRatio: PropTypes.bool,
    heroImageBackgroundColor: PropTypes.string,
    maintainListImageAspectRatio: PropTypes.bool,
    addMetaTags: PropTypes.bool,
    videoIconPlacement: PropTypes.string,
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    }),
    titleType: PropTypes.string,
    spans: PropTypes.object
  }

  static contextTypes = {
    flux: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired
  }

  static defaultProps = {
    title: '',
    headline: 'Default Headline',
    categoryUrl: '',
    titleColor: '#555',
    textColor: '#000',
    startArticleIndex: 0,
    types: ['story', 'clip', 'link'],
    numberOfItems: 7,
    layout: 'list',
    showReadMore: false,
    showUpdatedDate: false,
    showMoreArrow: true,
    showTitle: true,
    linkToLegacy: false,
    enableStackView: false,
    maintainHeroImageAspectRatio: false,
    maintainListImageAspectRatio: false,
    heroImageBackgroundColor: '#FFF',
    addMetaTags: false,
    videoIconPlacement: 'center',
    titleType: TITLE_TYPE.MODULE_TITLE
  }

  componentWillMount() {
    const {
      FRN_rawResponses: [
        {
          data: {
            headline = '',
            link = '',
            seo: {
              description = '',
              keywords = '',
              robot = '',
            } = {}
          } = {}
        } = {}
      ] = [],
      addMetaTags
    } = this.props;

    if (addMetaTags) {
      setMetaTags(this, [{
        name: 'description',
        content: description
      }, {
        name: 'keywords',
        content: keywords
      }, {
        name: 'robots',
        content: robot
      }, {
        rel: 'canonical',
        href: getCanonicalLink(get(this, 'props.FRN_rawResponses[0].data', {})),
        isLink: true
      }, {
        property: 'og:title',
        content: headline
      }, {
        property: 'og:type',
        content: 'article'
      }, {
        property: 'og:url',
        content: link
      }, {
        property: 'og:description',
        content: description
      }, {
        name: 'twitter:card',
        content: 'summary_large_image'
      }, {
        name: 'twitter:description',
        content: description
      }, {
        name: 'twitter:title',
        content: headline
      }]);
    }
  }

  render() {
    const {
      FRN_rawResponses: [
        {
          data: {
            features: items = [],
            headline = ''
          } = {}
        } = {}
      ] = [],
      title,
      headline: headlineType,
      titleColor,
      textColor,
      layout,
      numberOfItems,
      startArticleIndex,
      types,
      displaySizes = [],
      backgroundColor,
      showPillsOnHeroItem,
      showDescriptions,
      showTitle,
      showThumbnails,
      showReadMore,
      showMoreArrow,
      readMoreText,
      readMoreBackgroundColor,
      readMoreTextColor,
      thumbnailAlignment,
      thumbnailHtmlWidth,
      showBylines,
      showUpdatedDate,
      categoryUrl,
      linkToLegacy,
      enableStackView,
      maintainHeroImageAspectRatio,
      heroImageBackgroundColor,
      maintainListImageAspectRatio,
      videoIconPlacement,
      timestampOptions = {},
      titleType,
      spans,
      fill
    } = this.props;

    const offset = Math.max(startArticleIndex, 0);
    const filteredItems = filterItems(items, { type: types, displaysize: displaySizes});
    const itemsToShow = filteredItems.slice(offset, numberOfItems + offset);
    const fillColor = fill || textColor;

    if (!itemsToShow.length) {
      return null;
    }

    const componentTitle = title || headline;
    let childComponent;

    if (layout === 'hero' && itemsToShow.length) {
      childComponent = (
        <HeroLayout
          headlineType={headlineType}
          items={itemsToShow}
          enableStackView={enableStackView}
          showPillsOnHeroItem={showPillsOnHeroItem}
          showDescriptions={showDescriptions}
          showThumbnails={showThumbnails}
          thumbnailAlignment={thumbnailAlignment}
          thumbnailHtmlWidth={thumbnailHtmlWidth}
          showBylines={showBylines}
          showUpdatedDate={showUpdatedDate}
          layout={layout}
          backgroundColor={backgroundColor}
          textColor={textColor}
          linkToLegacy={linkToLegacy}
          maintainHeroImageAspectRatio={maintainHeroImageAspectRatio}
          maintainListImageAspectRatio={maintainListImageAspectRatio}
          heroImageBackgroundColor={heroImageBackgroundColor}
          videoIconPlacement={videoIconPlacement}
          timestampOptions={timestampOptions}
          spans={spans}
          fill={fillColor}
        />
      );
    } else {
      childComponent = (
        <List
          headlineType={headlineType}
          items={itemsToShow}
          showDescriptions={showDescriptions}
          showThumbnails={showThumbnails}
          thumbnailAlignment={thumbnailAlignment}
          thumbnailHtmlWidth={thumbnailHtmlWidth}
          showBylines={showBylines}
          showUpdatedDate={showUpdatedDate}
          layout={layout}
          backgroundColor={backgroundColor}
          textColor={textColor}
          linkToLegacy={linkToLegacy}
          maintainListImageAspectRatio={maintainListImageAspectRatio}
          videoIconPlacement={videoIconPlacement}
          timestampOptions={timestampOptions}
        />
      );
    }

    return (
      <div className="ArticleList" style={{backgroundColor}}>
        { showTitle && componentTitle ? <ComponentTitle titleType={titleType} showArrow={showMoreArrow} color={titleColor} title={componentTitle} link={categoryUrl ? categoryUrl : undefined} /> : null }
        {childComponent}
        { showReadMore ?
          <ReadMoreButton
            link={categoryUrl}
            textColor={readMoreTextColor}
            text={readMoreText}
            backgroundColor={readMoreBackgroundColor} /> : null}
      </div>
    );
  }
}

class List extends Component {
  static propTypes = {
    headlineType: PropTypes.string,
    items: PropTypes.array.isRequired,
    showBylines: PropTypes.bool,
    showUpdatedDate: PropTypes.bool,
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    }),
    showThumbnails: PropTypes.bool,
    thumbnailAlignment: PropTypes.string,
    thumbnailHtmlWidth: PropTypes.number,
    layout: PropTypes.string,
    showDescriptions: PropTypes.bool,
    textColor: PropTypes.string,
    linkToLegacy: PropTypes.bool,
    maintainListImageAspectRatio: PropTypes.bool,
    videoIconPlacement: PropTypes.string
  }

  static defaultProps = {
    headlineType: '',
    items: [],
    showBylines: true,
    showThumbnails: false,
    thumbnailAlignment: 'right',
    layout: 'list',
    showDescriptions: true,
    textColor: '#000',
    linkToLegacy: false,
    maintainListImageAspectRatio: false,
    videoIconPlacement: 'center'
  }

  render() {
    const {
      headlineType,
      items,
      showBylines,
      showUpdatedDate,
      showThumbnails,
      timestampOptions = {},
      thumbnailAlignment,
      thumbnailHtmlWidth,
      layout,
      showDescriptions,
      textColor,
      linkToLegacy,
      maintainListImageAspectRatio,
      videoIconPlacement } = this.props;

    const itemComponents =
      map(items, (item, i) =>
        <Item key={i}
              headlineType={headlineType}
              item={item}
              showBylines={showBylines}
              showUpdatedDate={showUpdatedDate}
              showThumbnails={showThumbnails}
              thumbnailAlignment={thumbnailAlignment}
              thumbnailHtmlWidth={thumbnailHtmlWidth}
              layout={layout}
              showDescriptions={showDescriptions}
              textColor={textColor}
              linkToLegacy={linkToLegacy}
              maintainImageAspectRatio={maintainListImageAspectRatio}
              videoIconPlacement = {videoIconPlacement}
              timestampOptions={timestampOptions}
        />);

    return (
      <div className="List">
        {itemComponents}
      </div>
    );
  }
}

class HeroLayout extends Component {
  static propTypes = {
    headlineType: PropTypes.string,
    items: PropTypes.array.isRequired,
    showBylines: PropTypes.bool,
    showUpdatedDate: PropTypes.bool,
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    }),
    showThumbnails: PropTypes.bool,
    thumbnailAlignment: PropTypes.string,
    thumbnailHtmlWidth: PropTypes.number,
    showPillsOnHeroItem: PropTypes.bool,
    layout: PropTypes.string,
    showDescriptions: PropTypes.bool,
    textColor: PropTypes.string,
    linkToLegacy: PropTypes.bool,
    enableStackView: PropTypes.bool,
    maintainHeroImageAspectRatio: PropTypes.bool,
    heroImageBackgroundColor: PropTypes.string,
    maintainListImageAspectRatio: PropTypes.bool,
    videoIconPlacement: PropTypes.string,
    spans: PropTypes.object,
    fill: PropTypes.string
  }

  static defaultProps = {
    headlineType: '',
    items: [],
    showBylines: true,
    showThumbnails: false,
    thumbnailAlignment: 'right',
    showPillsOnHeroItem: false,
    layout: 'list',
    showDescriptions: true,
    textColor: '#000',
    linkToLegacy: false,
    enableStackView: false,
    maintainHeroImageAspectRatio: false,
    heroImageBackgroundColor: '#FFF',
    maintainListImageAspectRatio: false,
    videoIconPlacement: 'center'
  }

  render() {
    const { items,
            headlineType,
            showBylines,
            showUpdatedDate,
            timestampOptions = {},
            showThumbnails,
            thumbnailAlignment,
            thumbnailHtmlWidth,
            showPillsOnHeroItem,
            layout,
            showDescriptions,
            linkToLegacy,
            textColor,
            spans,
            enableStackView,
            maintainHeroImageAspectRatio,
            heroImageBackgroundColor,
            maintainListImageAspectRatio,
            videoIconPlacement,
            fill
          } = this.props;
    const headItems = head(items);
    const tailItems = tail(items);

    const HeroItemComponent = (<HeroItem item={headItems}
              headlineType={headlineType}
              showBylines={showBylines}
              showUpdatedDate={showUpdatedDate}
              showPillsOnHeroItem={showPillsOnHeroItem}
              showThumbnails={showThumbnails}
              thumbnailAlignment={thumbnailAlignment}
              layout={layout}
              enableStackView={enableStackView}
              showDescriptions={showDescriptions}
              textColor={textColor}
              linkToLegacy={linkToLegacy}
              maintainHeroImageAspectRatio={maintainHeroImageAspectRatio}
              heroImageBackgroundColor={heroImageBackgroundColor}
              videoIconPlacement = {videoIconPlacement}
              timestampOptions={timestampOptions}
              spans={spans}
              fill={fill}
              />);

    const ListComponent = (<List items={tailItems}
          headlineType={headlineType}
          showBylines={showBylines}
          showUpdatedDate={showUpdatedDate}
          showThumbnails={showThumbnails}
          thumbnailAlignment={thumbnailAlignment}
          thumbnailHtmlWidth={thumbnailHtmlWidth}
          layout={layout}
          showDescriptions={showDescriptions}
          textColor={textColor}
          linkToLegacy={linkToLegacy}
          maintainListImageAspectRatio={maintainListImageAspectRatio}
          videoIconPlacement = {videoIconPlacement}
          timestampOptions={timestampOptions}
          />);

    return (
      <div>
      {
        !enableStackView ?
          <div className="HeroLayout">
            <div className="HeroLayout-heroCell">
              {HeroItemComponent}
            </div>
            <div className="HeroLayout-listContainer">
              {ListComponent}
            </div>
          </div>
        :
          <div className="HeroLayout" style={{display: enableStackView ? 'block' : null}}>
            {HeroItemComponent}
            {ListComponent}
           </div>
        }
        </div>
    );
  }
}

class HeroItem extends Component {
  static propTypes = {
    item: PropTypes.object.isRequired,
    headlineType: PropTypes.string,
    showPillsOnHeroItem: PropTypes.bool,
    showDescriptions: PropTypes.bool,
    showReadMore: PropTypes.bool,
    showBylines: PropTypes.bool,
    showUpdatedDate: PropTypes.bool,
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    }),
    textColor: PropTypes.string,
    linkToLegacy: PropTypes.bool,
    enableStackView: PropTypes.bool,
    maintainHeroImageAspectRatio: PropTypes.bool,
    heroImageBackgroundColor: PropTypes.string,
    videoIconPlacement: PropTypes.string,
    spans: PropTypes.object,
    fill: PropTypes.string
  }

  static contextTypes = {
    config: PropTypes.object.isRequired
  }

  static defaultProps = {
    headlineType: '',
    showPillsOnHeroItem: false,
    showDescriptions: true,
    showReadMore: false,
    showBylines: false,
    enableStackView: false,
    textColor: '#000',
    maintainHeroImageAspectRatio: false,
    heroImageBackgroundColor: '#FFF',
    videoIconPlacement: 'center'
  }

  render() {
    const {
      headlineType,
      item: {
        id = '',
        url = '',
        type = '',
        link = '',
        layout = '',
        headline = '',
        bylines = [],
        images = {},
        updatedDate = '',
        publishedDate = '',
        abstract: previewText = '',
        seo: { pageurl: slug = ''} = {},
        contentClassification: category = '',
        surfaceable: [ { type: featureType = '' } = {} ] = [],
        abridged: {
          headline: abridgedHeadline = '',
        } = {},
        abstractimage: {
          filename: newsImage = '',
          lastEditedDate = ''
        } = {},
        window: {
          isnewwindow = false,
          height = '500',
          width = '500',
          showtoolbar = true,
          showscrollbar = true,
          showlocation = true,
          isresizable = true
        } = {},
        options: {
          date: {
            published: {
              showonabstract: showPublished = true
            } = {},
            updated: {
              showonabstract: showUpdated = true
            } = {}
          } = {}
        } = {}
      } = {},
      spans = {},
      showBylines,
      showReadMore,
      showDescriptions,
      showUpdatedDate,
      enableStackView,
      linkToLegacy = false,
      showPillsOnHeroItem,
      fill,
      textColor: color = '#000',
      maintainHeroImageAspectRatio = false,
      heroImageBackgroundColor = '#FFFFFF',
      videoIconPlacement,
      timestampOptions: {
        showElapsedTime,
        displayShortDateTime
      } = {}
    } = this.props;

    const {
      config: {
        appOptions: {
          placeholderImage = ''
        } = {}
      } = {}
    } = this.context;

    const isVideoAttached = type.toLowerCase() === 'clip' || featureType.toLowerCase() === 'clip';
    const imageContainerClassName = classNames(
      'HeroItem-imageContainer',
      { [`VideoThumbnail VideoThumbnail-${videoIconPlacement}`]: isVideoAttached },
      { 'HeroItem-imageStackContainer': enableStackView },
      { 'HeroItem-imageStackContainer--noImage': enableStackView && !newsImage }
    );
    const authors = map(bylines, bylineToString);
    const textStyles = {color, fill};

    const customBackgroundStyles = {};

    customBackgroundStyles['backgroundColor'] = heroImageBackgroundColor;

    let imageStyles;

    if (maintainHeroImageAspectRatio) {
      imageStyles = { objectFit: 'contain' };
    }

    let timestamp;

    if (showBylines) {
      const publishedDateTimestamp = showPublished ? publishedDate : null;
      const updatedDateTimestamp = showUpdated ? updatedDate : null;

      const timestampDate = showUpdatedDate ? updatedDateTimestamp : publishedDateTimestamp;
      timestamp = <Timestamp publishDate={timestampDate} authors={authors} showElapsedTime={showElapsedTime} displayShortDateTime={displayShortDateTime} />;
    }

    const isShowWindowLayout = isnewwindow && (type === 'link');
    const iframeImage = get(images, 'graphic.filename', '');
    const linkImage = get(images, 'abstract.filename', '');

    const urlIFrameOrUrlLoc = layout === 3 ? (url || iframeImage) : link;

    const dataOfWindowOpen = getDataOfWindowOpen(width, height, showtoolbar, showscrollbar, showlocation, isresizable);

    const heroItemClassName = classNames('HeroItem', {
      'HeroItem-hoverHeroItem': isShowWindowLayout
    });

    /* Number of columns for each breakpoint, default: xs = 12, sm = xs, md = sm, lg = md, xl = lg */
    const {
      xs = 12,
      sm = xs,
      md = sm,
      lg = md,
      xl = lg
    } = spans;

    /* at min-with: 911px and not stack view, Hero Image takes 65% width of Article list */
    const ratioImageWidth = enableStackView ? 1 : 0.65;

    /* Breakpoints: XS: 0-767, S: 768-991, M: 992-1199, L: 1200-1469, XL: 1470-  */
    const imageBreakpoints = [
      { "maxWidth": 640, "widthVW": 100 },
      { "maxWidth": 767, "widthVW": Math.round((xs / 12) * 100) },
      { "maxWidth": 991, "widthPX": Math.round((sm / 12) * 720 * ratioImageWidth)},
      { "maxWidth": 1199, "widthPX": Math.round((md / 12) * 940 * ratioImageWidth)},
      { "maxWidth": 1469, "widthPX": Math.round((lg / 12) * 1140 * ratioImageWidth)},
      {                   "widthPX": Math.round((xl / 12) * 1410 * ratioImageWidth)},
    ];

    const isOnClick = () => {isShowWindowLayout && showWindow(urlIFrameOrUrlLoc, '_blank', dataOfWindowOpen);};
    const imageUrl = linkImage || newsImage || placeholderImage;

    const headlineToDisplay = headlineType === 'Abridged Headline' ?
      (abridgedHeadline || headline) : headline;

    return (
      <div onClick={isOnClick} className={heroItemClassName} style={{position: enableStackView ? 'static' : null}}>
        <div className="HeroItem-container">
          <div className="HeroItem-imageCell">
            <div className={imageContainerClassName} style={customBackgroundStyles}>
              {
                imageUrl ?
                <Image
                  imageBreakpoints={imageBreakpoints}
                  src={imageUrl}
                  lastEditedDate={lastEditedDate}
                  alt={headlineToDisplay}
                  imageStyles={imageStyles}
                  imageClassNames="img-responsive"
                  ignoreObjectFit
                /> :
                null
              }
              {showPillsOnHeroItem ? <ArticleListItemCategoryPill categories={category} /> : null}
            </div>
          </div>
          <div className="HeroItem-textCell" style={textStyles}>
            <h4 className="HeroItem-title">{headlineToDisplay}</h4>
              {showDescriptions ? <p className="HeroItem-abstract" >
                <span className="summary" dangerouslySetInnerHTML={{ __html: previewText.replace(/p>/g, 'span>') }} />
                {showReadMore ? <span className="readMore">Read more</span> : null}
              </p> : null}
              {timestamp}
          </div>
          <ArticleLink article={{ id, type, slug, link: isShowWindowLayout ? null : urlIFrameOrUrlLoc }} linkToLegacy={linkToLegacy} />
        </div>
      </div>
    );
  }
}

/**
 * Class representing a preview of a single news article, with a link to the full thing.
 */
class Item extends Component {
  static propTypes = {
    headlineType: PropTypes.string,
    item: PropTypes.object.isRequired,
    showThumbnails: PropTypes.bool,
    thumbnailAlignment: PropTypes.string,
    thumbnailHtmlWidth: PropTypes.number,
    layout: PropTypes.string,
    headline: PropTypes.string,
    previewText: PropTypes.string,
    showDescriptions: PropTypes.bool,
    showBylines: PropTypes.bool,
    showUpdatedDate: PropTypes.bool,
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    }),
    updatedDate: PropTypes.string,
    publishDate: PropTypes.string,
    authors: PropTypes.array,
    showReadMore: PropTypes.bool,
    bylines: PropTypes.array,
    textColor: PropTypes.string,
    linkToLegacy: PropTypes.bool,
    maintainImageAspectRatio: PropTypes.bool,
    videoIconPlacement: PropTypes.string
  }

  static contextTypes = {
    config: PropTypes.object.isRequired
  }

  render() {
    const {
      headlineType = '',
      item: {
        id = '',
        url = '',
        type = '',
        link = '',
        layout = '',
        headline = '',
        updatedDate = '',
        publishedDate = '',
        abstract: previewText = '',
        bylines = [],
        seo: { pageurl: slug = ''} = {},
        surfaceable: [ { type: featureType = '' } = {} ] = [],
        abridged: {
          headline: abridgedHeadline = '',
        } = {},
        abstractimage: {
          filename: newsImage = '',
          lastEditedDate = '',
        } = {},
        images = {},
        options: {
          date: {
            published: {
              showonabstract: showPublished = true
            } = {},
            updated: {
              showonabstract: showUpdated = true
            } = {}
          } = {}
        } = {},
        window: {
          isnewwindow = false,
          height = '500',
          width = '500',
          showtoolbar = true,
          showscrollbar = true,
          showlocation = true,
          isresizable = true
        } = {}
      } = {},
      showUpdatedDate,
      thumbnailHtmlWidth,
      showBylines = false,
      showReadMore = false,
      linkToLegacy = false,
      showDescriptions = true,
      textColor = '#000',
      thumbnailAlignment = 'right',
      videoIconPlacement = 'center',
      maintainImageAspectRatio = false,
      showThumbnails = false,
      timestampOptions: {
        showElapsedTime,
        displayShortDateTime
      } = {}
    } = this.props;
    const headlineToDisplay = headlineType === 'Abridged Headline' ?
      (abridgedHeadline || headline) : headline;
    const {
      config: {
        appOptions: {
          placeholderImage = ''
        } = {}
      } = {}
    } = this.context;

    const linkImage = get(images, 'abstract.filename', '');
    const authors = map(bylines, bylineToString);

    const isVideoAttached = type.toLowerCase() === 'clip' || featureType.toLowerCase() === 'clip';
    const textContainerClassName = classNames('textContainer', 'clearfix', {
      'no-image': !showThumbnails
    });
    const imageCellClassName = classNames('Item-imageCell', {
      [`image-${thumbnailAlignment}`]: thumbnailAlignment
    }, {
      'Item-maintainImageAspectRatio': maintainImageAspectRatio
    });
    const imageContainerClassName = classNames('Item-imageContainer', {
      [`VideoThumbnail VideoThumbnail-${videoIconPlacement}`]: isVideoAttached
    });

    const iframeImage = get(images, 'graphic.filename', '');
    const isShowWindowLayout = isnewwindow && (type === 'link');
    const urlIFrameOrUrlLoc = layout === 3 ? (url || iframeImage) : link;

    const dataOfWindowOpen = getDataOfWindowOpen(width, height, showtoolbar, showscrollbar, showlocation, isresizable);

    const itemClassName = classNames('Item', {
      'ArticleList-hoverArticleList': isShowWindowLayout
    });

    let timestamp;

    if (showBylines) {
      const publishedDateTimestamp = showPublished ? publishedDate : null;
      const updatedDateTimestamp = showUpdated ? updatedDate : null;

      const timestampDate = showUpdatedDate ? updatedDateTimestamp : publishedDateTimestamp;
      timestamp = <Timestamp publishDate={timestampDate} authors={authors} showElapsedTime={showElapsedTime} displayShortDateTime={displayShortDateTime} />;
    }

    return (
      <div onClick={() => isShowWindowLayout && showWindow(urlIFrameOrUrlLoc, '_blank', dataOfWindowOpen)} className={itemClassName}>
        <div className="Item-container" style={{minHeight: showThumbnails ? '106px' : null}}>
          <ArticleLink article={{ id, type, slug, link: isShowWindowLayout ? null : urlIFrameOrUrlLoc }} linkToLegacy={linkToLegacy} />
          <div className={textContainerClassName}>
            {
              showThumbnails ?
              <div className={imageCellClassName}>
                <div className={imageContainerClassName}>
                <Image width={thumbnailHtmlWidth || 180} src={ linkImage || newsImage || placeholderImage } lastEditedDate={lastEditedDate} alt={headline} imageClassNames="img-responsive" />
                </div>
              </div>
              : null
            }
            <div className="ItemTextArea" style={{color: textColor}}>
              <div className="articleTitle">
              <h4 className="ItemTextArea-title">{headlineToDisplay}</h4>
              </div>
              {
                showDescriptions ?
                <div className="descriptionText">
                  <p className="ItemTextArea-abstract">
                    <span className="summary" dangerouslySetInnerHTML={{ __html: previewText.replace(/p>/g, 'span>') }} />
                    {showReadMore ? <span className="readMore">Read more</span> : null}
                  </p>
                </div>
                : null
              }
              {timestamp}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

class ArticleLink extends Component {
  static propTypes = {
    article: PropTypes.object.isRequired,
    linkToLegacy: PropTypes.bool
  }

  render() {
    const {
      article: {
        type,
        id,
        slug,
        link: fallbackLink
      } = {},
      linkToLegacy = false
    } = this.props;

    const href = (type !== 'link' && (typeof location !== 'undefined' && !linkToLegacy)) ?
      location.protocol + '//' + location.hostname + (location.port ? ':' + location.port : '') + '/' + type + '/' + id + '/' + slug :
      fallbackLink;

    return (
      <a className="ArticleList-ArticleLink ItemLink" href={href} target="_top" />
    );
  }
}

/**
 * Class representing a category label for a news article cell, displayed over the image
 */
class ArticleListItemCategoryPill extends Component {
  static propTypes = {
    categories: PropTypes.node.isRequired,
    backgroundColor: PropTypes.string
  }

  render() {
    const { categories, backgroundColor } = this.props;

    let customBackgroundStyles = {};

    if (backgroundColor) {
      customBackgroundStyles['backgroundColor'] = backgroundColor;
    }

    return (
      <div className="ArticleListItemCategoryPill" style={customBackgroundStyles}>
        { isArray(categories) ? head(categories) : categories}
      </div>
    );
  }
}

export default ArticleList;



// WEBPACK FOOTER //
// ./app/components/news/ArticleList.js